/**
 * SEO Configuration for Universo Oro
 * Centralized URL patterns, meta templates, and slug helpers
 */

// =============================================================================
// URL PATTERNS & ROUTE DEFINITIONS
// =============================================================================

export const SEO_ROUTES = {
  // Homepage
  home: '/',
  
  // Orologeria Section
  orologeria: {
    landing: '/orologeria',
    catalog: '/orologeria/in-vendita',
    detail: '/orologeria/in-vendita/:anno/:marca/:modello/:referenza/:condizione/:codice',
    valutazione: '/orologeria/valutazione',
    valutazioneDetail: '/orologeria/valutazione/:marca/:modello',
    // Brand-specific selling pages
    vendiBrand: '/orologeria/vendi/:brand',
  },
  
  // Gioielleria Section
  gioielleria: {
    landing: '/gioielleria',
    categoria: '/gioielleria/:categoria',
    detail: '/gioielleria/:categoria/:materiale/:codice',
  },
  
  // Compro Oro Section
  comproOro: {
    landing: '/compro-oro-argento',
    valutazione: '/compro-oro-argento/valutazione',
    valutazioneCitta: '/compro-oro-argento/valutazione/:citta',
    valutazioneMetallo: '/compro-oro-argento/valutazione/:citta/:metallo/:titolo',
    testimonianze: '/compro-oro-argento/testimonianze',
  },
  
  // Investimenti Section
  investimenti: {
    landing: '/investire-in-oro',
    lingotti: '/investire-in-oro/lingotti',
    lingottoDetail: '/investire-in-oro/lingotti/:raffineria/:peso',
    monete: '/investire-in-oro/monete',
    monetaDetail: '/investire-in-oro/monete/:paese/:taglio',
  },
  
  // Info Pages
  info: {
    chiSiamo: '/chi-siamo',
    doveSiamo: '/dove-siamo',
    contatti: '/contatti',
  },
  
  // Utility
  thankYou: '/thank-you',
} as const;

// =============================================================================
// LEGACY URL REDIRECTS (301)
// =============================================================================

export const LEGACY_REDIRECTS: Record<string, string> = {
  // Old service pages
  '/servizio/orologeria': '/orologeria',
  '/vendere-comprare-orologi-lusso-roma': '/orologeria/in-vendita',
  '/watch-shop': '/orologeria/in-vendita',
  // Product aliases (/watch/:id, /orologi/:id) need a DB lookup. They are
  // handled in server/index.ts, never redirected blindly to the catalog.
  
  // Old jewelry pages
  // '/gioielleria' stays the same
  
  // Old compro oro pages  
  '/servizio/compro-oro': '/compro-oro-argento',
  '/compro-oro-argento-roma-valutazione': '/compro-oro-argento/valutazione',
  
  // Old investment pages
  '/servizio/investimenti': '/investire-in-oro',
  '/lingotti-oro-monete-investimento': '/investire-in-oro',
  
  // Brand selling pages
  '/vendi-rolex': '/orologeria/vendi/rolex',
  '/vendi-omega': '/orologeria/vendi/omega',
  '/vendi-patek-philippe': '/orologeria/vendi/patek-philippe',
  '/vendi-audemars-piguet': '/orologeria/vendi/audemars-piguet',
  '/vendi-cartier': '/orologeria/vendi/cartier',
  '/vendi-iwc': '/orologeria/vendi/iwc',
};

// =============================================================================
// SLUG HELPERS
// =============================================================================

/**
 * Converts a string to a URL-safe slug
 * @example slugify("Rolex Submariner") => "rolex-submariner"
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove multiple hyphens
    .replace(/^-|-$/g, ''); // Trim hyphens from ends
}

/**
 * Converts a slug back to readable text
 * @example deslugify("rolex-submariner") => "Rolex Submariner"
 */
export function deslugify(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// =============================================================================
// URL BUILDERS
// =============================================================================

export interface WatchUrlParams {
  anno: number | string | null | undefined;
  marca: string;
  modello: string;
  referenza: string;
  condizione: string;
  codice: string;
}

export function buildWatchDetailUrl(params: WatchUrlParams): string {
  const year = Number(params.anno);
  const yearSegment = Number.isInteger(year) && year >= 1800 && year <= 2100 ? String(year) : 'anno-sconosciuto';
  return `/orologeria/in-vendita/${yearSegment}/${slugify(params.marca || 'orologio')}/${slugify(params.modello || 'modello')}/${slugify(params.referenza || 'ref')}/${slugify(params.condizione || 'usato')}/${encodeURIComponent(params.codice)}`;
}

/** Same URL for cards, SSR, search, redirects and sitemaps. */
export function watchCanonicalUrl(watch: {
  id: string | number; yearOfProduction?: string | number | null;
  brand: string; model: string; referenceNumber?: string | null; condition?: string | null;
}): string {
  return buildWatchDetailUrl({
    anno: watch.yearOfProduction,
    marca: watch.brand,
    modello: watch.model,
    referenza: watch.referenceNumber || 'ref',
    condizione: watch.condition || 'usato',
    codice: String(watch.id),
  });
}

export function parseWatchDetailUrl(url: string): WatchUrlParams | null {
  const match = url.match(/^\/orologeria\/in-vendita\/(\d{4}|anno-sconosciuto)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)$/);
  if (!match) return null;
  
  return {
    anno: match[1] === 'anno-sconosciuto' ? null : parseInt(match[1]),
    marca: deslugify(match[2]),
    modello: deslugify(match[3]),
    referenza: deslugify(match[4]),
    condizione: deslugify(match[5]),
    codice: match[6],
  };
}

// Watch Catalog URL Builders (for filtered catalog pages)
export function buildBrandCatalogUrl(brand: string): string {
  return `/orologeria/in-vendita/${slugify(brand)}`;
}

/** Slugs are lossy: resolve against published names, never deslugify to query the catalog. */
export function resolveWatchBrandSlug(slug: string, publishedBrands: string[]): string | null {
  return publishedBrands.find(brand => slugify(brand) === slug) ?? null;
}

export function parseBrandFromCatalogUrl(url: string): string | null {
  const match = url.match(/\/orologeria\/in-vendita\/([^/]+)$/);
  if (!match) return null;
  return deslugify(match[1]);
}

export interface JewelryUrlParams {
  categoria: string;
  materiale: string;
  codice: string;
}

export function buildJewelryDetailUrl(params: JewelryUrlParams): string {
  return `/gioielleria/${slugify(params.categoria)}/${slugify(params.materiale)}/${params.codice}`;
}

export interface InvestmentUrlParams {
  tipo: 'lingotto' | 'moneta';
  raffineria?: string;
  peso?: string;
  paese?: string;
  taglio?: string;
}

export function buildInvestmentDetailUrl(params: InvestmentUrlParams): string {
  if (params.tipo === 'lingotto' && params.raffineria && params.peso) {
    return `/investire-in-oro/lingotti/${slugify(params.raffineria)}/${slugify(params.peso)}`;
  }
  if (params.tipo === 'moneta' && params.paese && params.taglio) {
    return `/investire-in-oro/monete/${slugify(params.paese)}/${slugify(params.taglio)}`;
  }
  return '/investire-in-oro';
}

// =============================================================================
// META TAG TEMPLATES
// =============================================================================

export interface MetaConfig {
  title: string;
  description: string;
  keywords?: string;
  ogType?: 'website' | 'product' | 'article';
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  canonical?: string;
  noIndex?: boolean;
}

import { PUBLIC_BUSINESS } from './public-business';

const BASE_TITLE_SUFFIX = ' | Universo Oro Roma';
export const DEFAULT_OG_IMAGE = '/og-image.jpg';

export const META_TEMPLATES = {
  // Homepage - Optimized for SEO (title 50-60 chars, description 120-160 chars)
  home: {
    title: 'Compro Oro Roma | Orologi Lusso | Universo Oro',
    description: 'Universo Oro Roma: compro oro e argento, orologi di lusso e lingotti. Richiedi una valutazione in sede.',
    keywords: 'compro oro roma, orologi lusso roma, investimenti oro, lingotti oro, universo oro, rolex roma, patek philippe roma, valutazione oro gratuita',
    ogType: 'website' as const,
    ogImage: '/og-image.jpg',
  },
  
  // Orologeria
  orologeriaLanding: {
    title: 'Orologi di Lusso Roma - Compravendita Rolex, Patek Philippe, Omega' + BASE_TITLE_SUFFIX,
    description: 'Acquista e vendi orologi di lusso a Roma. Consulta il catalogo o richiedi una valutazione.',
    keywords: 'orologi lusso roma, compro rolex roma, vendo patek philippe, orologi secondo polso roma',
    ogType: 'website' as const,
  },
  
  orologeriaCatalog: {
    title: 'Orologi di Lusso in Vendita Roma - Catalogo Rolex, Patek, AP' + BASE_TITLE_SUFFIX,
    description: 'Scopri il nostro catalogo di orologi di lusso. Verifica disponibilità e condizioni nelle schede prodotto.',
    keywords: 'orologi lusso vendita roma, rolex usato roma, patek philippe usato, catalogo orologi',
    ogType: 'website' as const,
  },
  
  orologeriaValutazione: {
    title: 'Valutazione Orologi di Lusso Gratuita Roma - Vendi il Tuo Orologio' + BASE_TITLE_SUFFIX,
    description: 'Valutazione gratuita del tuo orologio di lusso a Roma. Rolex, Omega, Patek Philippe, Cartier. Pagamento immediato tramite bonifico istantaneo.',
    keywords: 'valutazione orologi roma, vendo rolex roma, quotazione orologi lusso',
    ogType: 'website' as const,
  },

  lavoraConNoi: {
    title: 'Lavora con Noi - Orologeria e Relazioni Commerciali | Universo Oro Roma',
    description: 'Candidati per collaborare con Universo Oro nella gestione e finalizzazione di contatti per acquisto e vendita di orologi. Colloqui online o a Roma.',
    keywords: 'lavoro orologeria roma, lavora con noi orologi, commerciale orologi lusso',
    ogType: 'website' as const,
  },
  
  // Gioielleria
  gioielleriaLanding: {
    title: 'Gioielleria di Lusso Roma - Anelli, Bracciali, Collane in Oro' + BASE_TITLE_SUFFIX,
    description: 'Gioielli di lusso a Roma. Anelli, bracciali, collane e orecchini in oro 18kt, oro bianco, argento 925. Creazioni esclusive e pezzi unici.',
    keywords: 'gioielleria roma, gioielli oro roma, anelli oro 18kt, bracciali lusso roma',
    ogType: 'website' as const,
  },
  
  // Compro Oro
  comproOroLanding: {
    title: 'Compro Oro e Argento Roma - Valutazione Gratuita, Pagamento Immediato' + BASE_TITLE_SUFFIX,
    description: 'Compro oro e argento a Roma. Consulta i prezzi indicativi di acquisto al grammo per titolo e richiedi una valutazione in sede.',
    keywords: 'compro oro roma, compro argento roma, valutazione oro roma, vendere oro roma',
    ogType: 'website' as const,
  },
  
  comproOroValutazione: {
    title: 'Valutazione Oro e Argento Online Roma - Calcola il Valore' + BASE_TITLE_SUFFIX,
    description: 'Calcola una stima di acquisto per oro e argento in base a peso e titolo. Il valore definitivo dipende dalla verifica in sede.',
    keywords: 'valutazione oro online, quotazione oro oggi, calcolo valore oro, prezzo oro al grammo',
    ogType: 'website' as const,
    canonical: '/compro-oro-argento/valutazione',
  },

  vendiOrologio: {
    title: 'Vendi il Tuo Orologio di Lusso - Valutazione Gratuita Roma' + BASE_TITLE_SUFFIX,
    description: 'Vendi il tuo orologio di lusso a Roma. Rolex, Omega, Patek Philippe. Richiedi una valutazione.',
    keywords: 'vendi orologio roma, valutazione orologio, vendi rolex, compro orologi lusso',
    ogType: 'website' as const,
  },
  
  // Investimenti
  investimentiLanding: {
    title: 'Oro Fisico Roma - Lingotti e Monete' + BASE_TITLE_SUFFIX,
    description: 'Scopri lingotti e monete in oro fisico a Roma. Prezzi di vendita indicativi e dettagli disponibili per prodotto.',
    keywords: 'investire in oro roma, lingotti oro roma, monete oro investimento, sterline oro',
    ogType: 'website' as const,
  },
  
  investimentiLingotti: {
    title: 'Lingotti Oro a Roma - Catalogo' + BASE_TITLE_SUFFIX,
    description: 'Consulta il catalogo dei lingotti in oro e richiedi informazioni su disponibilità, caratteristiche e prezzo di vendita.',
    keywords: 'lingotti oro roma, comprare lingotti oro, oro fisico',
    ogType: 'product' as const,
  },
  
  investimentiMonete: {
    title: 'Monete Oro da Investimento Roma - Sterline, Marenghi, Krugerrand' + BASE_TITLE_SUFFIX,
    description: 'Monete in oro a Roma: consulta le caratteristiche e la disponibilità dei prodotti nel catalogo.',
    keywords: 'monete oro roma, sterline oro, marenghi oro, krugerrand roma',
    ogType: 'product' as const,
  },
  
  // Info Pages
  chiSiamo: {
    title: 'Chi Siamo - Universo Oro Roma' + BASE_TITLE_SUFFIX,
    description: 'Scopri Universo Oro e i servizi di compravendita di metalli preziosi e orologi a Roma.',
    keywords: 'universo oro storia, compro oro roma storia, chi siamo universo oro',
    ogType: 'website' as const,
  },
  
  doveSiamo: {
    title: 'Dove Siamo - Sede operativa a Roma' + BASE_TITLE_SUFFIX,
    description: `Trova la sede operativa di Universo Oro a Roma: ${PUBLIC_BUSINESS.addressRome}. Consulta orari e indicazioni prima della visita.`,
    keywords: 'universo oro roma indirizzo, compro oro viale eritrea, sede universo oro',
    ogType: 'website' as const,
  },
  
  contatti: {
    title: 'Contatti Universo Oro Roma - Richiedi Informazioni' + BASE_TITLE_SUFFIX,
    description: `Contatta Universo Oro Roma per valutazioni, informazioni o appuntamenti. Tel: ${PUBLIC_BUSINESS.phone}. Email: ${PUBLIC_BUSINESS.email}`,
    keywords: 'contatti universo oro, telefono compro oro roma, email universo oro',
    ogType: 'website' as const,
  },
  
  // Utility Pages (Thank You pages - should not be indexed)
  thankYou: {
    title: 'Grazie - Universo Oro Roma',
    description: 'Grazie per aver compilato il modulo di valutazione. Il nostro team ti contatterà presto con una proposta personalizzata.',
    keywords: 'grazie, valutazione, universo oro',
    ogType: 'website' as const,
    noIndex: true,
  },
  
  newsletterThankYou: {
    title: 'Iscrizione Confermata - Newsletter Universo Oro',
    description: 'Grazie per esserti iscritto alla newsletter di Universo Oro. Riceverai aggiornamenti esclusivi su quotazioni oro e opportunità uniche.',
    keywords: 'newsletter, iscrizione, universo oro',
    ogType: 'website' as const,
    noIndex: true,
  },
  
  // Service Pages (generic, can be enhanced for specific services)
  servizi: {
    title: 'Servizi Universo Oro Roma - Orologeria, Oro, Investimenti' + BASE_TITLE_SUFFIX,
    description: 'Scopri i servizi di Universo Oro: compro oro, orologeria e lingotti in oro fisico.',
    keywords: 'servizi universo oro, compro oro, orologeria, investimenti oro',
    ogType: 'website' as const,
  },
  
  // Catalog page aliases (for specific catalog pages)
  WATCH_CATALOG: {
    title: 'Orologi di Lusso in Vendita Roma - Catalogo Rolex, Patek, AP' + BASE_TITLE_SUFFIX,
    description: 'Scopri il catalogo di orologi di lusso e verifica le caratteristiche nelle schede prodotto.',
    keywords: 'orologi lusso vendita roma, rolex usato roma, patek philippe usato, catalogo orologi',
    ogType: 'website' as const,
  },
  
  OROLOGI_USATI: {
    title: 'Orologi Usati di Lusso Roma - Compra Vendi Rolex, Omega, Patek' + BASE_TITLE_SUFFIX,
    description: 'Orologi usati di lusso a Roma. Consulta disponibilità e condizioni di ciascun prodotto.',
    keywords: 'orologi usati roma, rolex usato roma, omega usato, patek philippe usato roma, orologi secondo polso',
    ogType: 'website' as const,
  },
  
  GIOIELLERIA: {
    title: 'Gioielleria di Lusso Roma - Anelli, Bracciali, Collane in Oro' + BASE_TITLE_SUFFIX,
    description: 'Gioielli di lusso a Roma. Anelli, bracciali, collane e orecchini in oro 18kt, oro bianco, argento 925. Creazioni esclusive e pezzi unici.',
    keywords: 'gioielleria roma, gioielli oro roma, anelli oro 18kt, bracciali lusso roma',
    ogType: 'website' as const,
  },
  
  INVESTIRE_ORO: {
    title: 'Investire in Oro Fisico Roma - Lingotti e Monete Certificati' + BASE_TITLE_SUFFIX,
    description: 'Scopri lingotti e monete in oro fisico a Roma. Consulta prezzi e caratteristiche dei prodotti.',
    keywords: 'investire in oro roma, lingotti oro roma, monete oro investimento, sterline oro',
    ogType: 'website' as const,
  },
} as const;

// =============================================================================
// DYNAMIC META GENERATORS
// =============================================================================

export interface WatchMetaParams {
  marca: string;
  modello: string;
  referenza: string;
  anno: number | null;
  condizione: string;
  prezzo?: number;
  immagine?: string;
}

export function generateWatchMeta(params: WatchMetaParams): MetaConfig {
  const { marca, modello, referenza, anno, condizione, prezzo, immagine } = params;
  const prezzoStr = prezzo ? ` - €${prezzo.toLocaleString('it-IT')}` : '';
  
  const yearText = anno ? ` ${anno}` : '';
  return {
    title: `${marca} ${modello} ${referenza}${yearText} ${condizione}${prezzoStr}` + BASE_TITLE_SUFFIX,
    description: `Acquista ${marca} ${modello} referenza ${referenza}${anno ? ` anno ${anno}` : ''} in condizioni ${condizione.toLowerCase()}. Verifica i dettagli della scheda prodotto.`,
    keywords: `${marca.toLowerCase()} ${modello.toLowerCase()}, ${marca.toLowerCase()} usato roma, comprare ${marca.toLowerCase()} ${modello.toLowerCase()}`,
    ogType: 'product',
    ogImage: immagine,
    ogImageWidth: 1200,
    ogImageHeight: 630,
  };
}

export interface JewelryMetaParams {
  tipo: string;
  materiale: string;
  nome?: string;
  prezzo?: number;
  immagine?: string;
}

export function generateJewelryMeta(params: JewelryMetaParams): MetaConfig {
  const { tipo, materiale, nome, prezzo, immagine } = params;
  const nomeStr = nome ? `${nome} - ` : '';
  const prezzoStr = prezzo ? ` - €${prezzo.toLocaleString('it-IT')}` : '';
  
  return {
    title: `${nomeStr}${tipo} in ${materiale}${prezzoStr}` + BASE_TITLE_SUFFIX,
    description: `${tipo} in ${materiale} disponibile presso Universo Oro Roma. Consulta i dettagli della scheda prodotto.`,
    keywords: `${tipo.toLowerCase()} ${materiale.toLowerCase()}, gioielli ${materiale.toLowerCase()} roma`,
    ogType: 'product',
    ogImage: immagine,
    ogImageWidth: 1200,
    ogImageHeight: 630,
  };
}

export interface InvestmentMetaParams {
  tipo: 'lingotto' | 'moneta';
  nome: string;
  peso?: string;
  prezzo?: number;
  immagine?: string;
}

export function generateInvestmentMeta(params: InvestmentMetaParams): MetaConfig {
  const { tipo, nome, peso, prezzo, immagine } = params;
  const pesoStr = peso ? ` ${peso}` : '';
  const prezzoStr = prezzo ? ` - €${prezzo.toLocaleString('it-IT')}` : '';
  
  const tipoLabel = tipo === 'lingotto' ? 'Lingotto' : 'Moneta';
  
  return {
    title: `${tipoLabel} Oro ${nome}${pesoStr}${prezzoStr}` + BASE_TITLE_SUFFIX,
    description: `${tipoLabel} oro ${nome}${pesoStr}. Consulta caratteristiche e prezzo di vendita presso Universo Oro Roma.`,
    keywords: `${tipo} oro ${nome.toLowerCase()}, investire oro ${nome.toLowerCase()}, comprare ${tipo} oro roma`,
    ogType: 'product',
    ogImage: immagine,
    ogImageWidth: 1200,
    ogImageHeight: 630,
  };
}

// =============================================================================
// BREADCRUMB DEFINITIONS
// =============================================================================

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export const BREADCRUMB_BASE: BreadcrumbItem = {
  name: 'Universo Oro',
  url: '/',
};

export function generateBreadcrumbs(path: string, customItems?: BreadcrumbItem[]): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [BREADCRUMB_BASE];
  
  if (customItems) {
    return [...breadcrumbs, ...customItems];
  }
  
  // Auto-generate from path
  const segments = path.split('/').filter(Boolean);
  let currentPath = '';
  
  const segmentLabels: Record<string, string> = {
    'orologeria': 'Orologeria',
    'in-vendita': 'In Vendita',
    'valutazione': 'Valutazione',
    'vendi': 'Vendi il Tuo Orologio',
    'gioielleria': 'Gioielleria',
    'compro-oro-argento': 'Compro Oro e Argento',
    'investire-in-oro': 'Investire in Oro',
    'lingotti': 'Lingotti',
    'monete': 'Monete',
    'chi-siamo': 'Chi Siamo',
    'dove-siamo': 'Dove Siamo',
    'contatti': 'Contatti',
    'testimonianze': 'Testimonianze',
  };
  
  for (const segment of segments) {
    currentPath += `/${segment}`;
    const label = segmentLabels[segment] || deslugify(segment);
    breadcrumbs.push({ name: label, url: currentPath });
  }
  
  return breadcrumbs;
}

// =============================================================================
// JSON-LD SCHEMA GENERATORS
// =============================================================================

export function generateBreadcrumbJsonLd(breadcrumbs: BreadcrumbItem[], baseUrl: string): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': `${baseUrl}${item.url}`,
    })),
  };
}

export interface ProductJsonLdParams {
  name: string;
  description: string;
  image?: string;
  brand?: string;
  price?: number;
  currency?: string;
  condition?: 'NewCondition' | 'UsedCondition' | 'RefurbishedCondition';
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  sku?: string;
}

export function generateProductJsonLd(params: ProductJsonLdParams, baseUrl: string): object {
  const {
    name,
    description,
    image,
    brand,
    price,
    currency = 'EUR',
    condition = 'UsedCondition',
    availability = 'InStock',
    sku,
  } = params;
  
  const jsonLd: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    'name': name,
    'description': description,
    'brand': brand ? { '@type': 'Brand', 'name': brand } : undefined,
    'itemCondition': `https://schema.org/${condition}`,
    'seller': {
      '@type': 'Organization',
      'name': 'Universo Oro',
      'url': baseUrl,
    },
  };
  
  if (image) {
    jsonLd.image = image;
  }
  
  if (sku) {
    jsonLd.sku = sku;
  }
  
  if (price) {
    jsonLd.offers = {
      '@type': 'Offer',
      'price': price,
      'priceCurrency': currency,
      'availability': `https://schema.org/${availability}`,
      'seller': {
        '@type': 'Organization',
        'name': 'Universo Oro',
      },
    };
  }
  
  return jsonLd;
}

// Canonical host — always www to avoid entity fragmentation across SSR and client.
const CANONICAL_BASE_URL = 'https://www.universo-oro.it';

export function generateLocalBusinessJsonLd(_baseUrl?: string): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${CANONICAL_BASE_URL}/#organization`,
    'name': 'UNIVERSO ORO S.r.l.',
    'alternateName': 'Universo Oro Roma',
    'url': CANONICAL_BASE_URL,
    'logo': `${CANONICAL_BASE_URL}/logo-email.png`,
    'description': 'Compravendita di oro, argento, orologi e lingotti a Roma.',
    'telephone': '+390686219969',
    'email': 'info@universo-oro.it',
    'vatID': 'IT02089140517',
    // NOTE (July 2026 audit): foundingDate intentionally omitted (1982 vs 1992
    // conflict — pending management confirmation via SEO Control Center) and
    // aggregateRating intentionally removed (hardcoded 4.9/327 was not from a
    // verified schema.org-compliant source; self-serving ratings risk a Google
    // manual action). Do NOT re-add either as hardcoded values.
    'identifier': [
      { '@type': 'PropertyValue', 'name': 'REA', 'value': 'AR-160913' },
      { '@type': 'PropertyValue', 'name': 'OAM OCO', 'value': 'OCO1206' },
      { '@type': 'PropertyValue', 'name': 'OAM OPO', 'value': 'OPO276' },
      { '@type': 'PropertyValue', 'name': 'P.IVA', 'value': '02089140517' },
    ],
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': PUBLIC_BUSINESS.streetRome,
      'addressLocality': 'Roma',
      'addressRegion': 'RM',
      'postalCode': '00199',
      'addressCountry': 'IT',
    },
    'areaServed': {
      '@type': 'City',
      'name': 'Roma',
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Servizi Universo Oro',
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': { '@type': 'Service', 'name': 'Compro Oro e Argento', 'description': 'Valutazione di oro e argento usato in sede' },
        },
        {
          '@type': 'Offer',
          'itemOffered': { '@type': 'Service', 'name': 'Compravendita Orologi di Lusso', 'description': 'Acquisto e vendita orologi Rolex, Patek Philippe, Omega e altri brand' },
        },
        {
          '@type': 'Offer',
          'itemOffered': { '@type': 'Service', 'name': 'Oro fisico', 'description': 'Lingotti e monete in oro fisico' },
        },
        {
          '@type': 'Offer',
          'itemOffered': { '@type': 'Service', 'name': 'Banco Metalli', 'description': 'Servizio professionale per operatori del settore' },
        },
      ],
    },
  };
}

/** @deprecated Use generateLocalBusinessJsonLd instead */
export function generateOrganizationJsonLd(baseUrl: string): object {
  return generateLocalBusinessJsonLd(baseUrl);
}

// =============================================================================
// CANONICAL URL HELPERS
// =============================================================================

export function getCanonicalUrl(path: string, baseUrl: string): string {
  // Normalize path
  let normalizedPath = path
    .toLowerCase()
    .replace(/\/+$/, '') // Remove trailing slashes
    .replace(/\/+/g, '/'); // Remove duplicate slashes
  
  // Handle index/home
  if (normalizedPath === '' || normalizedPath === '/') {
    normalizedPath = '';
  }
  
  return `${baseUrl}${normalizedPath}`;
}

// =============================================================================
// SITEMAP HELPERS
// =============================================================================

export interface SitemapEntry {
  url: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

/**
 * Canonical registry of all indexable public static routes.
 * This is the single source of truth consumed by both the XML sitemap
 * (getStaticSitemapEntries) and the HTML sitemap page (sitemap.tsx).
 * Authentication/account surfaces are intentionally excluded.
 */
export interface PublicStaticRoute {
  url: string;
  label: string;
  /** Groups routes into named sections used by the HTML sitemap */
  section: 'orologeria' | 'gioielleria' | 'compro-oro' | 'investimenti' | 'info' | 'legal';
  changefreq?: SitemapEntry['changefreq'];
  priority?: number;
}

export const PUBLIC_STATIC_ROUTES: PublicStaticRoute[] = [
  // Orologeria
  { url: '/orologeria', label: 'Panoramica Orologeria', section: 'orologeria', changefreq: 'daily', priority: 0.9 },
  { url: '/orologeria/in-vendita', label: 'Orologi in Vendita', section: 'orologeria', changefreq: 'daily', priority: 0.9 },
  { url: '/orologeria/valutazione', label: 'Valuta il Tuo Orologio', section: 'orologeria', changefreq: 'weekly', priority: 0.8 },
  { url: '/orologeria/vendi/rolex', label: 'Vendi Rolex', section: 'orologeria', changefreq: 'monthly', priority: 0.8 },
  { url: '/orologeria/vendi/omega', label: 'Vendi Omega', section: 'orologeria', changefreq: 'monthly', priority: 0.7 },
  { url: '/orologeria/vendi/patek-philippe', label: 'Vendi Patek Philippe', section: 'orologeria', changefreq: 'monthly', priority: 0.8 },
  { url: '/orologeria/vendi/audemars-piguet', label: 'Vendi Audemars Piguet', section: 'orologeria', changefreq: 'monthly', priority: 0.7 },
  { url: '/orologeria/vendi/cartier', label: 'Vendi Cartier', section: 'orologeria', changefreq: 'monthly', priority: 0.7 },
  { url: '/orologeria/vendi/iwc', label: 'Vendi IWC', section: 'orologeria', changefreq: 'monthly', priority: 0.7 },
  { url: '/orologeria/vendi/breitling', label: 'Vendi Breitling', section: 'orologeria', changefreq: 'monthly', priority: 0.6 },
  { url: '/orologeria/vendi/tag-heuer', label: 'Vendi TAG Heuer', section: 'orologeria', changefreq: 'monthly', priority: 0.6 },
  { url: '/orologeria/vendi/panerai', label: 'Vendi Panerai', section: 'orologeria', changefreq: 'monthly', priority: 0.6 },
  { url: '/orologeria/vendi/hublot', label: 'Vendi Hublot', section: 'orologeria', changefreq: 'monthly', priority: 0.6 },

  // Gioielleria
  { url: '/gioielleria', label: 'Catalogo Gioielli', section: 'gioielleria', changefreq: 'daily', priority: 0.9 },
  { url: '/gioielleria/anelli', label: 'Anelli', section: 'gioielleria', changefreq: 'weekly', priority: 0.7 },
  { url: '/gioielleria/bracciali', label: 'Bracciali', section: 'gioielleria', changefreq: 'weekly', priority: 0.7 },
  { url: '/gioielleria/collane', label: 'Collane', section: 'gioielleria', changefreq: 'weekly', priority: 0.7 },
  { url: '/gioielleria/orecchini', label: 'Orecchini', section: 'gioielleria', changefreq: 'weekly', priority: 0.7 },
  { url: '/gioielleria/spille', label: 'Spille', section: 'gioielleria', changefreq: 'weekly', priority: 0.6 },
  { url: '/gioielleria/pendenti', label: 'Pendenti', section: 'gioielleria', changefreq: 'weekly', priority: 0.6 },
  { url: '/gioielleria/gemelli', label: 'Gemelli', section: 'gioielleria', changefreq: 'weekly', priority: 0.6 },

  // Compro Oro
  { url: '/compro-oro-argento', label: 'Valutazione Oro e Argento', section: 'compro-oro', changefreq: 'weekly', priority: 0.9 },
  { url: '/compro-oro-argento/valutazione', label: 'Quotazioni in Tempo Reale', section: 'compro-oro', changefreq: 'daily', priority: 0.8 },

  // Investimenti
  { url: '/investire-in-oro', label: "Panoramica Investimenti", section: 'investimenti', changefreq: 'weekly', priority: 0.9 },
  { url: '/investire-in-oro/lingotti', label: "Lingotti d'Oro", section: 'investimenti', changefreq: 'weekly', priority: 0.8 },
  { url: '/investire-in-oro/monete', label: "Monete d'Oro", section: 'investimenti', changefreq: 'weekly', priority: 0.8 },

  // Servizi hub
  { url: '/servizi', label: 'I Nostri Servizi', section: 'info', changefreq: 'monthly', priority: 0.8 },

  // Banco Metalli
  { url: '/banco-metalli', label: 'Banco Metalli Autorizzato', section: 'compro-oro', changefreq: 'weekly', priority: 0.9 },

  // Info
  { url: '/chi-siamo', label: 'La Nostra Storia', section: 'info', changefreq: 'monthly', priority: 0.5 },
  { url: '/dove-siamo', label: 'Dove Siamo', section: 'info', changefreq: 'monthly', priority: 0.5 },
  { url: '/contatti', label: 'Contatti', section: 'info', changefreq: 'monthly', priority: 0.6 },
  { url: '/lavora-con-noi', label: 'Lavora con Noi', section: 'info', changefreq: 'monthly', priority: 0.5 },
  { url: '/sicurezza-trasparenza', label: 'Sicurezza e Trasparenza', section: 'info', changefreq: 'monthly', priority: 0.4 },

  // Legal & trust
  { url: '/privacy', label: 'Privacy Policy', section: 'legal', changefreq: 'monthly', priority: 0.4 },
  { url: '/cookie', label: 'Cookie Policy', section: 'legal', changefreq: 'monthly', priority: 0.3 },
  { url: '/termini', label: 'Termini e Condizioni', section: 'legal', changefreq: 'monthly', priority: 0.4 },
  { url: '/politica-resi', label: 'Politica Resi', section: 'legal', changefreq: 'monthly', priority: 0.3 },
  { url: '/garanzie', label: 'Garanzie e Certificazioni', section: 'legal', changefreq: 'monthly', priority: 0.4 },
  { url: '/codice-etico', label: 'Codice Etico', section: 'legal', changefreq: 'monthly', priority: 0.3 },
  { url: '/mog-231', label: 'MOG 231', section: 'legal', changefreq: 'monthly', priority: 0.3 },
  { url: '/whistleblowing', label: 'Whistleblowing', section: 'legal', changefreq: 'monthly', priority: 0.3 },

  // HTML sitemap itself
  { url: '/mappa-del-sito', label: 'Mappa del Sito', section: 'info', changefreq: 'monthly', priority: 0.3 },
];

export function getStaticSitemapEntries(): SitemapEntry[] {
  const entries: SitemapEntry[] = [
    { url: '/', changefreq: 'daily', priority: 1.0 },
  ];
  for (const route of PUBLIC_STATIC_ROUTES) {
    // The /orologeria overview renders the same catalog as /in-vendita.
    if (route.url === '/orologeria') continue;
    entries.push({
      url: route.url,
      changefreq: route.changefreq ?? 'monthly',
      priority: route.priority ?? 0.5,
    });
  }
  return entries;
}

// Export watch brands for URL generation
export const WATCH_BRANDS = [
  'rolex', 'omega', 'patek-philippe', 'audemars-piguet', 
  'cartier', 'iwc', 'breitling', 'tag-heuer', 'panerai',
  'jaeger-lecoultre', 'vacheron-constantin', 'hublot'
] as const;

// Export jewelry categories
export const JEWELRY_CATEGORIES = [
  'anelli', 'bracciali', 'collane', 'orecchini', 
  'spille', 'pendenti', 'gemelli'
] as const;

// Export investment products
export const INVESTMENT_COUNTRIES = [
  'regno-unito', 'italia', 'sudafrica', 'austria', 
  'usa', 'messico', 'cile', 'svizzera', 'francia'
] as const;

export type WatchBrand = typeof WATCH_BRANDS[number];
export type JewelryCategory = typeof JEWELRY_CATEGORIES[number];
export type InvestmentCountry = typeof INVESTMENT_COUNTRIES[number];
